/**
 * `mediaTileData.ts` — web mirror of Unity's `Models/Data/MediaTileData.cs`
 * + `Models/Data/MediaAssetData.cs`, and the Dynamo attribute mapping in
 * `DynamoClient.ToMediaTileData` / `ToMediaTileItem` / `ToMediaAssetData`
 * (`DynamoClient.cs:4474-4546`).
 *
 * Cards ("Add card") and action buttons are **media tiles**, not entity
 * snapshots:
 * a per-scene `MediaTileData` row (transform + presentation) joined to a
 * per-project `MediaAssetData` row (text / image content) via `AssetId`.
 *
 * Rows:
 *   - tile:  pk `{customerId}:project`, sk `$project:{p}:scene:{s}:mediatile:{id}`
 *   - asset: pk `{customerId}:project`, sk `$project:{p}:mediaasset:{id}`
 */

import type { Vec3 } from "./entityData";
import type { RgbaColor } from "./skyboxData";
import { toNameFloatMap, vec3From } from "./nameFloatMap";

/** Unity `MediaAssetType` serialized values. */
export const MEDIA_ASSET_TYPE = Object.freeze({
  Text: 0,
  ActionButton: 1,
} as const);
export type MediaAssetType = (typeof MEDIA_ASSET_TYPE)[keyof typeof MEDIA_ASSET_TYPE];

/** Unity `MediaTileData.Placement` — serialized ints on `ImagePlacement`. */
export const MEDIA_TILE_PLACEMENT = Object.freeze({ Bottom: 0, Top: 1 } as const);
export type MediaTilePlacement =
  (typeof MEDIA_TILE_PLACEMENT)[keyof typeof MEDIA_TILE_PLACEMENT];

/** Unity `MediaTileData.DefaultScale` (creation-time scale). */
export const MEDIA_TILE_DEFAULT_SCALE = 1;
/** Unity `MediaTileData.DefaultBillboard` (new cards do not follow camera). */
export const MEDIA_TILE_DEFAULT_BILLBOARD = 0;
/** Dynamo read fallback when a tile row is missing `Scale` (`DynamoClient.cs:4487`). */
export const MEDIA_TILE_FALLBACK_SCALE = 0.003;
/** Unity `MediaTileData.DefaultBackgroundColor` — opaque black. */
export const MEDIA_TILE_DEFAULT_BACKGROUND: RgbaColor = Object.freeze({
  r: 0,
  g: 0,
  b: 0,
  a: 1,
});
/** `MediaTileContextViewModel.k_minWidth` / `k_maxWidth` (auto-width clamp). */
export const MEDIA_TILE_MIN_WIDTH = 100;
export const MEDIA_TILE_MAX_WIDTH = 200;

// ---------------------------------------------------------------------------
// Wire shapes (Dynamo rows after `unmarshall`)
// ---------------------------------------------------------------------------

/** Raw `mediatile` row (`ToMediaTileItem`, `DynamoClient.cs:4496`). */
export interface MediaTileDto {
  readonly ID: string;
  readonly AssetID?: string;
  readonly Name?: string;
  readonly Visibility?: boolean;
  readonly BillboardFactor?: number | string;
  readonly Width?: number | string;
  readonly FixedWidth?: boolean;
  readonly Scale?: number | string;
  readonly Locked?: boolean;
  readonly ForcedVisibility?: boolean;
  readonly ImagePlacement?: number | string;
  readonly Position?: Readonly<Record<string, number>>;
  /** Unity euler degrees `{x,y,z}` (written from `Rotation.eulerAngles`). */
  readonly Rotation?: Readonly<Record<string, number>>;
  readonly BgColor?: Readonly<Record<string, number>>;
  readonly [extra: string]: unknown;
}

/** Raw `mediaasset` row (`ToMediaAssetItem` / `ToMediaAssetData`). */
export interface MediaAssetDto {
  readonly ID: string;
  readonly MediaType?: number | string;
  readonly Text?: string;
  readonly ImageId?: string;
  readonly ActionDataId?: string;
  /** Unity writes `ModifiedTS` on create (image updates bump `ModifiedTimestamp`
   *  due to an upstream attribute-name mismatch; readers only use `ModifiedTS`). */
  readonly ModifiedTS?: string;
  readonly [extra: string]: unknown;
}

// ---------------------------------------------------------------------------
// Client shapes
// ---------------------------------------------------------------------------

export interface MediaAssetData {
  readonly id: string;
  readonly mediaType: MediaAssetType;
  readonly text: string;
  /** Texture id under `textures/{imageId}`; empty = no image. */
  readonly imageId: string;
  /** Scene action linked by an action-button asset; empty = unlinked. */
  readonly actionDataId: string;
  readonly modifiedAt: string;
}

export interface MediaTileData {
  readonly id: string;
  readonly assetId: string;
  readonly name: string;
  /** Unity world-space position (converted at the renderer boundary). */
  readonly position: Vec3;
  /** Unity euler degrees (ZXY application order, LH frame). */
  readonly rotationEuler: Vec3;
  /** Uniform scale (Unity persists `localScale.x` only). */
  readonly scale: number;
  readonly visibility: boolean;
  readonly isLocked: boolean;
  readonly forcedVisibility: boolean;
  /** `> 0.5` = billboard toward the camera (Unity `IsBillboard`). */
  readonly billboard: number;
  /** Tile width in canvas UI units (world width = width × 0.002 × scale). */
  readonly width: number;
  /** User adjusted the width — auto-grow from text stops. */
  readonly fixedWidth: boolean;
  readonly backgroundColor: RgbaColor;
  readonly imagePlacement: MediaTilePlacement;
  /** Joined at load time from `assetId` (Unity `MediaTileData.Asset`). */
  readonly asset: MediaAssetData;
}

/** Editable subset used by `IProjectModel.updateMediaTileAsync`. */
export interface MediaTilePatch {
  readonly name?: string;
  readonly position?: Vec3;
  readonly rotationEuler?: Vec3;
  readonly scale?: number;
  readonly visibility?: boolean;
  readonly isLocked?: boolean;
  readonly forcedVisibility?: boolean;
  readonly billboard?: number;
  readonly width?: number;
  readonly fixedWidth?: boolean;
  readonly backgroundColor?: RgbaColor;
  readonly imagePlacement?: MediaTilePlacement;
}

/**
 * Which event flags accompany an `update_mediatile` publish. Mirrors the
 * `UpdateEntityFlags` members Unity sends for media tiles
 * (`ProjectModel_MediaTiles.cs:346` `GenerateSharedModification`).
 */
export type MediaTileUpdateFlag =
  | "Transform"
  | "Visibility"
  | "Lock"
  | "DisplayName"
  | "MediaTile";

/** Unity `UpdateEntityFlags` bit values for the wire mask. */
export function mediaTileFlagMask(flags: ReadonlyArray<MediaTileUpdateFlag>): number {
  let mask = 0;
  if (flags.includes("Transform")) mask |= 1 << 0;
  if (flags.includes("Visibility")) mask |= 1 << 1;
  if (flags.includes("Lock")) mask |= 1 << 2;
  if (flags.includes("DisplayName")) mask |= 1 << 5;
  if (flags.includes("MediaTile")) mask |= 1 << 10;
  return mask;
}

// ---------------------------------------------------------------------------
// Mapping
// ---------------------------------------------------------------------------

export function createDefaultMediaAsset(id: string): MediaAssetData {
  return {
    id,
    mediaType: MEDIA_ASSET_TYPE.Text,
    text: "",
    imageId: "",
    actionDataId: "",
    modifiedAt: new Date().toISOString(),
  };
}

/** Mirrors `ToMediaTileData` read fallbacks (`DynamoClient.cs:4474`). */
export function mapMediaTileDtoToData(
  dto: MediaTileDto,
  asset: MediaAssetData,
): MediaTileData {
  return {
    id: dto.ID,
    assetId: dto.AssetID ?? asset.id,
    name: dto.Name ?? "",
    position: vec3From(toNameFloatMap(dto.Position), [0, 0, 0]),
    rotationEuler: vec3From(toNameFloatMap(dto.Rotation), [0, 0, 0]),
    scale: toNumber(dto.Scale, MEDIA_TILE_FALLBACK_SCALE),
    visibility: dto.Visibility ?? true,
    isLocked: dto.Locked ?? false,
    forcedVisibility: dto.ForcedVisibility ?? false,
    billboard: toNumber(dto.BillboardFactor, 1),
    width: Math.round(toNumber(dto.Width, MEDIA_TILE_MIN_WIDTH)),
    fixedWidth: dto.FixedWidth ?? false,
    backgroundColor: mapColor(dto.BgColor, MEDIA_TILE_DEFAULT_BACKGROUND),
    imagePlacement: mapPlacement(dto.ImagePlacement),
    asset,
  };
}

/** Mirrors `ToMediaTileItem` attribute names (`DynamoClient.cs:4496`). */
export function mediaTileDataToDto(tile: MediaTileData): MediaTileDto {
  return {
    ID: tile.id,
    AssetID: tile.assetId,
    Name: tile.name,
    Visibility: tile.visibility,
    BillboardFactor: tile.billboard,
    Width: tile.width,
    FixedWidth: tile.fixedWidth,
    Scale: tile.scale,
    Locked: tile.isLocked,
    ForcedVisibility: tile.forcedVisibility,
    ImagePlacement: tile.imagePlacement,
    Position: { x: tile.position[0], y: tile.position[1], z: tile.position[2] },
    Rotation: {
      x: tile.rotationEuler[0],
      y: tile.rotationEuler[1],
      z: tile.rotationEuler[2],
    },
    BgColor: {
      r: tile.backgroundColor.r,
      g: tile.backgroundColor.g,
      b: tile.backgroundColor.b,
      a: tile.backgroundColor.a,
    },
  };
}

export function mapMediaAssetDtoToData(dto: MediaAssetDto): MediaAssetData {
  return {
    id: dto.ID,
    mediaType: mapMediaAssetType(dto.MediaType),
    text: dto.Text ?? "",
    imageId: dto.ImageId ?? "",
    actionDataId: dto.ActionDataId ?? "",
    modifiedAt: dto.ModifiedTS ?? "",
  };
}

export function mediaAssetDataToDto(asset: MediaAssetData): MediaAssetDto {
  return {
    ID: asset.id,
    MediaType: asset.mediaType,
    Text: asset.text,
    ImageId: asset.imageId,
    ActionDataId: asset.actionDataId,
    ModifiedTS: asset.modifiedAt,
  };
}

/**
 * Decode a Unity-JsonUtility-serialized `MediaTileData` from a realtime
 * `create_mediatile` event payload. Field names come from the private
 * `[SerializeField]` names (`MediaTileData.cs`): `id`, `assetId`, `name`,
 * `scale`, `visibility`, `isLocked`, `forcedVisibility`, `billboard`,
 * `width`, `fixedWidth`, `bgColor`, `imagePlacement`, and the
 * `pos` / `euler` `NameFloatPair[]` arrays from `OnBeforeSerialize`.
 */
export function mapWireMediaTile(
  wire: Record<string, unknown>,
  asset: MediaAssetData,
): MediaTileData | null {
  const id = wireString(wire, "id", "Id", "ID");
  if (!id) return null;
  return {
    id,
    assetId: wireString(wire, "assetId", "AssetId", "AssetID") ?? asset.id,
    name: wireString(wire, "name", "Name") ?? "",
    position: vec3From(toNameFloatMap(wire.pos ?? wire.Position ?? wire.position), [0, 0, 0]),
    rotationEuler: vec3From(toNameFloatMap(wire.euler ?? wire.Rotation ?? wire.rotation), [0, 0, 0]),
    scale: toNumber(wire.scale ?? wire.Scale, MEDIA_TILE_DEFAULT_SCALE),
    visibility: wireBool(wire, true, "visibility", "Visibility"),
    isLocked: wireBool(wire, false, "isLocked", "IsLocked", "Locked"),
    forcedVisibility: wireBool(wire, false, "forcedVisibility", "ForcedVisibility"),
    billboard: toNumber(wire.billboard ?? wire.Billboard ?? wire.BillboardFactor, 1),
    width: Math.round(toNumber(wire.width ?? wire.Width, MEDIA_TILE_MIN_WIDTH)),
    fixedWidth: wireBool(wire, false, "fixedWidth", "FixedWidth"),
    backgroundColor: mapColor(
      (wire.bgColor ?? wire.BgColor ?? wire.BackgroundColor) as
        | Readonly<Record<string, number>>
        | undefined,
      MEDIA_TILE_DEFAULT_BACKGROUND,
    ),
    imagePlacement: mapPlacement(
      (wire.imagePlacement ?? wire.ImagePlacement ?? wire.placement) as number | string | undefined,
    ),
    asset,
  };
}

function mapPlacement(value: number | string | undefined): MediaTilePlacement {
  return toNumber(value, 0) === MEDIA_TILE_PLACEMENT.Top
    ? MEDIA_TILE_PLACEMENT.Top
    : MEDIA_TILE_PLACEMENT.Bottom;
}

function mapColor(
  value: Readonly<Record<string, number>> | undefined,
  fallback: RgbaColor,
): RgbaColor {
  if (!value) return fallback;
  const map = toNameFloatMap(value);
  return {
    r: typeof map.r === "number" ? map.r : fallback.r,
    g: typeof map.g === "number" ? map.g : fallback.g,
    b: typeof map.b === "number" ? map.b : fallback.b,
    a: typeof map.a === "number" ? map.a : fallback.a,
  };
}

function toNumber(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function wireString(
  wire: Record<string, unknown>,
  ...keys: ReadonlyArray<string>
): string | null {
  for (const key of keys) {
    const value = wire[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return null;
}

function wireBool(
  wire: Record<string, unknown>,
  fallback: boolean,
  ...keys: ReadonlyArray<string>
): boolean {
  for (const key of keys) {
    const value = wire[key];
    if (typeof value === "boolean") return value;
  }
  return fallback;
}

/**
 * Port of `ProjectModel.GetNewMediaTileName`: scan existing `"Card N"` or
 * `"Button N"` names and return the next free index.
 */
export function nextMediaTileName(
  existing: ReadonlyArray<MediaTileData>,
  mediaType: MediaAssetType = MEDIA_ASSET_TYPE.Text,
): string {
  const prefix =
    mediaType === MEDIA_ASSET_TYPE.ActionButton ? "Button" : "Card";
  let highestIndex = 1;
  for (const tile of existing) {
    if (!tile.name.startsWith(prefix)) continue;
    const parts = tile.name.split(" ");
    if (parts.length > 1) {
      const index = Number.parseInt(parts[1], 10);
      if (Number.isInteger(index) && index >= highestIndex) highestIndex = index + 1;
    }
  }
  return `${prefix} ${highestIndex}`;
}

function mapMediaAssetType(value: number | string | undefined): MediaAssetType {
  return toNumber(value, MEDIA_ASSET_TYPE.Text) ===
    MEDIA_ASSET_TYPE.ActionButton
    ? MEDIA_ASSET_TYPE.ActionButton
    : MEDIA_ASSET_TYPE.Text;
}
